import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import { message } from 'antd';
import { ActionGetPatientPlanDetails } from '../../../store/Actions/AppConfigAction';
import { ActionUpdateById } from '../../../store/Actions/CommonAction';
import { openPrintPreview } from '../../../components/Print';

export const usePatientPlanDetailsLogic = () => {
    const dispatch = useDispatch();
    const { id: orderId } = useParams();
    const { search } = useLocation();

    const searchParams = useMemo(() => new URLSearchParams(search), [search]);
    const guestId = searchParams.get('guestId') || searchParams.get('customerId');

    const { patientPlanDetails: planData, loader } = useSelector(state => state?.appConfig || {});

    // Local state to track changes before saving
    const [localPlanData, setLocalPlanData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (orderId && guestId) {
            dispatch(ActionGetPatientPlanDetails({ orderId, guestId }));
        }
    }, [dispatch, orderId, guestId]);

    // Update local state when remote data loads
    useEffect(() => {
        if (planData && planData.length > 0) {
            setLocalPlanData(JSON.parse(JSON.stringify(planData)));
        }
    }, [planData]);

    const patientInfo = useMemo(() => planData?.[0]?.customerId || {}, [planData]);

    // Helper to format 24h time to 12h AM/PM
    const formatTime = (timeStr) => {
        if (!timeStr) return 'N/A';
        return moment(timeStr, ['HH:mm', 'h:mm A']).format('hh:mm A');
    };

    /**
     * Updates local state with new medicine/status value.
     * Does NOT save to server directly.
     */
    const handlePlanChange = (planId, serviceId, date, newValue) => {
        setLocalPlanData(prev => prev.map(plan => {
            if (plan._id !== planId) return plan;

            const updatedServiceDetails = { ...plan.serviceDetails };
            if (updatedServiceDetails[date]) {
                updatedServiceDetails[date] = updatedServiceDetails[date].map(service =>
                    service._id === serviceId ? { ...service, medicineValue: newValue } : service
                );
            }

            return { ...plan, serviceDetails: updatedServiceDetails };
        }));
    };

    /**
     * Saves all changes to the server.
     */
    const handleSave = () => {
        if (!localPlanData || localPlanData.length === 0) return;

        setIsSaving(true);
        const hideLoading = message.loading('Saving treatment plans...', 0);

        // Prepare batch updates (one for each updated plan)
        const updatePromises = localPlanData.map(plan => {
            return new Promise((resolve) => {
                const payload = {
                    Query: `admin/patient-plan/${plan._id}`,
                    obj: {
                        serviceDetails: plan.serviceDetails
                    }
                };
                dispatch(ActionUpdateById(payload, (res) => resolve(res)));
            });
        });

        Promise.all(updatePromises)
            .then(results => {
                const successCount = results.filter(r => r?.statusCode === 200).length;
                if (successCount === results.length) {
                    message.success('All changes saved successfully');
                    // Refresh from server to ensure sync
                    dispatch(ActionGetPatientPlanDetails({ orderId, guestId }));
                } else {
                    message.warning(`${successCount}/${results.length} plans saved. Some errors occurred.`);
                }
            })
            .catch(() => message.error('Failed to save changes'))
            .finally(() => {
                setIsSaving(false);
                hideLoading();
            });
    };

    const handlePrint = () => {
        const dataToPrint = localPlanData.length > 0 ? localPlanData : planData;
        if (!dataToPrint || dataToPrint.length === 0) return;

        const sections = [
            {
                title: 'Patient Information',
                fields: [
                    { label: 'Name', value: patientInfo.name || 'N/A' },
                    { label: 'Registration No', value: patientInfo.registrationNo || 'N/A' },
                    { label: 'Gender', value: patientInfo.gender || 'N/A' },
                    { label: 'Birth Date', value: patientInfo.birthDate ? moment(patientInfo.birthDate).format('DD MMM YYYY') : 'N/A' },
                    { label: 'Phone', value: patientInfo.phoneNo || 'N/A' },
                    { label: 'Email', value: patientInfo.email || 'N/A' },
                ]
            }
        ];

        dataToPrint.forEach(plan => {
            const treatmentData = [];
            Object.entries(plan.serviceDetails || {}).forEach(([date, services]) => {
                services.forEach(service => {
                    treatmentData.push([
                        moment(date).format('DD MMM YYYY'),
                        service.serviceName,
                        `${formatTime(service.startTime)} - ${formatTime(service.endTime)}`,
                        service.medicineValue || 'Not Started'
                    ]);
                });
            });

            if (treatmentData.length > 0) {
                sections.push({
                    title: `Treatment: ${plan.treatmentName}`,
                    type: 'table',
                    headers: ['Date', 'Service', 'Time Slot', 'Status'],
                    data: treatmentData
                });
            }
        });

        openPrintPreview(
            {},
            `Patient Treatment Plan - ${patientInfo.name || 'Details'}`,
            { sections }
        );
    };

    return {
        orderId,
        guestId,
        planData: localPlanData, // Export local data so UI shows pending changes
        loader,
        patientInfo,
        handlePrint,
        formatTime,
        handlePlanChange,
        handleSave,
        isSaving,
        hasChanges: JSON.stringify(planData) !== JSON.stringify(localPlanData)
    };
};
